import React, { useState, useEffect } from 'react';
import { ADMIN_API_ENDPOINTS, FRONTEND_URL } from '../config/api';
import { getAdminToken } from '../services/apiService';

const SubscriptionManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState({
    title_en: '',
    title_zh: '',
    content_en: '',
    content_zh: '',
    subscription_type: 'promotional',
    image_en: '',
    image_zh: '',
  });
  const [uploadingImage, setUploadingImage] = useState({
    en: false,
    zh: false,
  });
  const [alert, setAlert] = useState({ type: '', text: '' });
  const [subscriberStats, setSubscriberStats] = useState({
    promotional: 0,
    product_updates: 0,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteMessageId, setDeleteMessageId] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);

  const subscriptionTypes = {
    promotional: '促销邮件 (Promotional Emails)',
    product_updates: '产品新闻 (Product News)',
  };

  // 获取订阅者统计
  const fetchSubscriberStats = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.SUBSCRIPTIONS.STATS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setSubscriberStats(
          data.stats || { promotional: 0, product_updates: 0 }
        );
      }
    } catch (error) {
      console.error('获取订阅者统计失败:', error);
    }
  };

  // 获取消息历史
  const fetchMessages = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.SUBSCRIPTIONS.MESSAGES, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        console.log('[SubscriptionManagement] 消息数据:', data.messages);
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('获取消息历史失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriberStats();
    fetchMessages();
  }, []);

  const handleInputChange = (e) => {
    setNewMessage({
      ...newMessage,
      [e.target.name]: e.target.value,
    });
  };

  // 处理图片上传
  const handleImageUpload = async (e, language) => {
    const file = e.target.files[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      setAlert({ type: 'error', text: '请上传图片文件' });
      setTimeout(() => setAlert({ type: '', text: '' }), 3000);
      return;
    }

    // 验证文件大小 (30MB)
    if (file.size > 30 * 1024 * 1024) {
      setAlert({ type: 'error', text: '图片大小不能超过30MB' });
      setTimeout(() => setAlert({ type: '', text: '' }), 3000);
      return;
    }

    setUploadingImage({ ...uploadingImage, [language]: true });

    try {
      const token = getAdminToken();
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(
        ADMIN_API_ENDPOINTS.SUBSCRIPTIONS.UPLOAD_MESSAGE_IMAGE,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (data.success) {
        setNewMessage({
          ...newMessage,
          [`image_${language}`]: data.imagePath,
        });
        setAlert({
          type: 'success',
          text: `${language === 'en' ? '英文' : '中文'}图片上传成功`,
        });
        setTimeout(() => setAlert({ type: '', text: '' }), 3000);
      } else {
        setAlert({ type: 'error', text: data.message || '图片上传失败' });
        setTimeout(() => setAlert({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Upload image error:', error);
      setAlert({ type: 'error', text: '网络错误，请稍后重试' });
      setTimeout(() => setAlert({ type: '', text: '' }), 3000);
    } finally {
      setUploadingImage({ ...uploadingImage, [language]: false });
    }
  };

  const handleCreateMessage = () => {
    setNewMessage({
      title_en: '',
      title_zh: '',
      content_en: '',
      content_zh: '',
      subscription_type: 'promotional',
      image_en: '',
      image_zh: '',
    });
    setShowCreateDialog(true);
  };

  const handleSendMessage = async () => {
    if (
      !newMessage.title_en ||
      !newMessage.title_zh ||
      !newMessage.content_en ||
      !newMessage.content_zh
    ) {
      setAlert({ type: 'error', text: '请填写完整的中英文标题和内容' });
      setTimeout(() => setAlert({ type: '', text: '' }), 3000);
      return;
    }

    setSending(true);
    try {
      const token = getAdminToken();
      const response = await fetch(
        ADMIN_API_ENDPOINTS.SUBSCRIPTIONS.SEND_MESSAGE,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(newMessage),
        }
      );
      const data = await response.json();

      if (data.success) {
        setAlert({
          type: 'success',
          text: `消息发送成功！已发送给 ${data.sentCount} 位用户`,
        });
        setTimeout(() => setAlert({ type: '', text: '' }), 5000);
        setShowCreateDialog(false);
        fetchMessages();
        fetchSubscriberStats();
      } else {
        setAlert({ type: 'error', text: data.message || '发送失败' });
        setTimeout(() => setAlert({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('发送消息失败:', error);
      setAlert({ type: 'error', text: '网络错误，请稍后重试' });
      setTimeout(() => setAlert({ type: '', text: '' }), 3000);
    } finally {
      setSending(false);
    }
  };

  // 删除消息处理
  const handleDeleteMessage = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(
        ADMIN_API_ENDPOINTS.SUBSCRIPTIONS.MESSAGE_BY_ID(deleteMessageId),
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setAlert({
          type: 'success',
          text: '消息已成功删除',
        });
        setTimeout(() => setAlert({ type: '', text: '' }), 3000);
        fetchMessages();
      } else {
        setAlert({ type: 'error', text: data.message || '删除失败' });
        setTimeout(() => setAlert({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('删除消息失败:', error);
      setAlert({ type: 'error', text: '网络错误，请稍后重试' });
      setTimeout(() => setAlert({ type: '', text: '' }), 3000);
    } finally {
      setShowDeleteConfirm(false);
      setDeleteMessageId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">加载中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 消息提示 */}
      {alert.text && (
        <div
          className={`px-6 py-4 rounded-lg flex items-center gap-3 ${alert.type === 'success'
            ? 'bg-green-500/20 border border-green-500 text-green-400'
            : 'bg-red-500/20 border border-red-500 text-red-400'
            }`}
        >
          {alert.type === 'success' ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 256 256"
            >
              <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"></path>
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 256 256"
            >
              <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-8-80V80a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm20,36a12,12,0,1,1-12-12A12,12,0,0,1,140,172Z"></path>
            </svg>
          )}
          <span>{alert.text}</span>
        </div>
      )}

      {/* 订阅者统计 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 text-sm mb-1">促销邮件订阅者</div>
              <div className="text-3xl font-bold text-purple-400">
                {subscriberStats.promotional}
              </div>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              fill="currentColor"
              className="text-purple-400 opacity-20"
              viewBox="0 0 256 256"
            >
              <path d="M224,48H32a8,8,0,0,0-8,8V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A8,8,0,0,0,224,48Zm-96,85.15L52.57,64H203.43ZM98.71,128,40,181.81V74.19Zm11.84,10.85,12,11.05a8,8,0,0,0,10.82,0l12-11.05,58,53.15H52.57ZM157.29,128,216,74.19V181.81Z"></path>
            </svg>
          </div>
        </div>
        <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 text-sm mb-1">产品新闻订阅者</div>
              <div className="text-3xl font-bold text-yellow-400">
                {subscriberStats.product_updates}
              </div>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              fill="currentColor"
              className="text-yellow-400 opacity-20"
              viewBox="0 0 256 256"
            >
              <path d="M96,104a8,8,0,0,1,8-8h64a8,8,0,0,1,0,16H104A8,8,0,0,1,96,104Zm8,40h64a8,8,0,0,0,0-16H104a8,8,0,0,0,0,16Zm128-76V184a16,16,0,0,1-16,16H40a16,16,0,0,1-16-16V68A16,16,0,0,1,40,52H68.69L90.93,29.76a16,16,0,0,1,22.63,0L136,52h80A16,16,0,0,1,232,68ZM68.69,68H187.31l-24-24L140,68a16,16,0,0,1-24,0ZM216,68H200V52a8,8,0,0,0-16,0V68H72V52a8,8,0,0,0-16,0V68H40V184H216Z"></path>
            </svg>
          </div>
        </div>
      </div>

      {/* 新建消息按钮 */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">消息历史</h2>
        <button
          onClick={handleCreateMessage}
          className="px-6 py-3 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg transition-colors flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="currentColor"
            viewBox="0 0 256 256"
          >
            <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
          </svg>
          新建消息
        </button>
      </div>

      {/* 消息列表 */}
      <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#12140f] border-b border-[#2a2d27]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  标题
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  类型
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  发送人数
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  发送时间
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2d27]">
              {messages.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    暂无消息记录
                  </td>
                </tr>
              ) : (
                messages.map((msg) => (
                  <tr
                    key={msg.id}
                    className="hover:bg-[#12140f] transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div
                        className="text-white font-medium"
                        title={msg.title_zh}
                      >
                        {msg.title_zh.length > 20
                          ? msg.title_zh.substring(0, 20) + '...'
                          : msg.title_zh}
                      </div>
                      <div
                        className="text-gray-400 text-sm"
                        title={msg.title_en}
                      >
                        {msg.title_en.length > 20
                          ? msg.title_en.substring(0, 20) + '...'
                          : msg.title_en}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-500/20 text-blue-400">
                        {subscriptionTypes[msg.subscription_type]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      {msg.sent_count || 0} 人
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(msg.created_at).toLocaleString('zh-CN')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            console.log(
                              '[SubscriptionManagement] 选中的消息:',
                              msg
                            );
                            console.log(
                              '[SubscriptionManagement] image_en:',
                              msg.image_en
                            );
                            console.log(
                              '[SubscriptionManagement] image_zh:',
                              msg.image_zh
                            );
                            setSelectedMessage(msg);
                            setShowDetailDialog(true);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 hover:border-blue-500/50 text-blue-400 hover:text-blue-300 rounded-lg text-xs font-medium transition-all duration-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 256 256"
                          >
                            <path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.47,133.47,0,0,1,25,128,133.33,133.33,0,0,1,48.07,97.25C70.33,75.19,97.22,64,128,64s57.67,11.19,79.93,33.25A133.46,133.46,0,0,1,231.05,128C223.84,141.46,192.43,192,128,192Zm0-112a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Z"></path>
                          </svg>
                          详情
                        </button>
                        <button
                          onClick={() => {
                            setDeleteMessageId(msg.id);
                            setShowDeleteConfirm(true);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/50 text-red-400 hover:text-red-300 rounded-lg text-xs font-medium transition-all duration-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 256 256"
                          >
                            <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                          </svg>
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 新建消息对话框 */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d17] border border-[#4a5f3a] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#12140f] border-b border-[#2a2d27] px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">新建订阅消息</h2>
              <button
                onClick={() => setShowCreateDialog(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* 订阅类型选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  消息类型 *
                </label>
                <select
                  name="subscription_type"
                  value={newMessage.subscription_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                >
                  <option value="promotional">
                    促销邮件 (Promotional Emails)
                  </option>
                  <option value="product_updates">
                    产品新闻 (Product News)
                  </option>
                </select>
                <p className="mt-2 text-sm text-gray-500">
                  将发送给所有订阅此类型的用户（
                  {subscriberStats[newMessage.subscription_type]} 人）
                </p>
              </div>

              {/* 英文标题 */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  标题（英文） *
                </label>
                <input
                  type="text"
                  name="title_en"
                  value={newMessage.title_en}
                  onChange={handleInputChange}
                  placeholder="Enter English title"
                  className="w-full px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#f5a623]"
                />
              </div>

              {/* 中文标题 */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  标题（中文） *
                </label>
                <input
                  type="text"
                  name="title_zh"
                  value={newMessage.title_zh}
                  onChange={handleInputChange}
                  placeholder="输入中文标题"
                  className="w-full px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#f5a623]"
                />
              </div>

              {/* 英文内容 */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  内容（英文） *
                </label>
                <textarea
                  name="content_en"
                  value={newMessage.content_en}
                  onChange={handleInputChange}
                  placeholder="Enter English content"
                  rows="6"
                  className="w-full px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#f5a623] resize-none"
                />
              </div>

              {/* 中文内容 */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  内容（中文） *
                </label>
                <textarea
                  name="content_zh"
                  value={newMessage.content_zh}
                  onChange={handleInputChange}
                  placeholder="输入中文内容"
                  rows="6"
                  className="w-full px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#f5a623] resize-none"
                />
              </div>

              {/* 英文图片上传 */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  英文消息图片（可选）
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-gray-400 cursor-pointer hover:border-[#f5a623] transition-colors flex items-center justify-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="currentColor"
                      viewBox="0 0 256 256"
                    >
                      <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                    </svg>
                    {uploadingImage.en ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>上传中...</span>
                      </>
                    ) : (
                      <span>选择英文图片</span>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'en')}
                      className="hidden"
                      disabled={uploadingImage.en}
                    />
                  </label>
                  {newMessage.image_en && (
                    <button
                      onClick={() =>
                        setNewMessage({ ...newMessage, image_en: '' })
                      }
                      className="px-3 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 rounded-lg transition-colors"
                      title="删除图片"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        viewBox="0 0 256 256"
                      >
                        <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                      </svg>
                    </button>
                  )}
                </div>
                {newMessage.image_en && (
                  <div className="mt-3 p-2 bg-[#262a22] border border-[#2a2d27] rounded-lg flex justify-center items-center">
                    <img
                      src={`${FRONTEND_URL}${newMessage.image_en}`}
                      alt="English preview"
                      className="max-w-full h-auto max-h-48 rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* 中文图片上传 */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  中文消息图片（可选）
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 px-4 py-3 bg-[#262a22] border border-[#2a2d27] rounded-lg text-gray-400 cursor-pointer hover:border-[#f5a623] transition-colors flex items-center justify-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="currentColor"
                      viewBox="0 0 256 256"
                    >
                      <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                    </svg>
                    {uploadingImage.zh ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>上传中...</span>
                      </>
                    ) : (
                      <span>选择中文图片</span>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'zh')}
                      className="hidden"
                      disabled={uploadingImage.zh}
                    />
                  </label>
                  {newMessage.image_zh && (
                    <button
                      onClick={() =>
                        setNewMessage({ ...newMessage, image_zh: '' })
                      }
                      className="px-3 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 rounded-lg transition-colors"
                      title="删除图片"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        viewBox="0 0 256 256"
                      >
                        <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                      </svg>
                    </button>
                  )}
                </div>
                {newMessage.image_zh && (
                  <div className="mt-3 p-2 bg-[#262a22] border border-[#2a2d27] rounded-lg flex justify-center items-center">
                    <img
                      src={`${FRONTEND_URL}${newMessage.image_zh}`}
                      alt="Chinese preview"
                      className="max-w-full h-auto max-h-48 rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="bg-[#0a0c0a] border-t border-[#2a2d27] px-6 py-4 flex justify-end gap-2">
              <button
                onClick={() => setShowCreateDialog(false)}
                className="px-6 py-3 bg-[#262a22] hover:bg-[#2a2d27] text-white rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSendMessage}
                disabled={sending}
                className="px-6 py-3 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {sending ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    发送中...
                  </>
                ) : (
                  '发送消息'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认对话框 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d17] border border-red-500/50 rounded-lg max-w-md w-full">
            <div className="bg-[#12140f] border-b border-[#2a2d27] px-6 py-4 flex items-center gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 256 256"
                className="text-red-400"
              >
                <path d="M236.8,188.09,149.35,36.22h0a24.76,24.76,0,0,0-42.7,0L19.2,188.09a23.51,23.51,0,0,0,0,23.72A24.35,24.35,0,0,0,40.55,224h174.9a24.35,24.35,0,0,0,21.33-12.19A23.51,23.51,0,0,0,236.8,188.09ZM120,104a8,8,0,0,1,16,0v40a8,8,0,0,1-16,0Zm8,88a12,12,0,1,1,12-12A12,12,0,0,1,128,192Z"></path>
              </svg>
              <h2 className="text-lg font-bold text-white">确认删除</h2>
            </div>

            <div className="p-6">
              <p className="text-gray-300 mb-4">确定要删除这条消息记录吗？</p>
              <p className="text-red-400 text-sm">
                此操作不可恢复，但不会影响已发送给用户的消息。
              </p>
            </div>

            <div className="bg-[#0a0c0a] border-t border-[#2a2d27] px-6 py-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteMessageId(null);
                }}
                className="px-6 py-3 bg-[#262a22] hover:bg-[#2a2d27] text-white rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleDeleteMessage}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                </svg>
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 消息详情对话框 */}
      {showDetailDialog && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* 头部 */}
            <div className="bg-[#12140f] border-b border-[#2a2d27] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                  className="text-blue-400"
                >
                  <path d="M224,48H32a8,8,0,0,0-8,8V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A8,8,0,0,0,224,48Zm-96,85.15L52.57,64H203.43ZM98.71,128,40,181.81V74.19Zm11.84,10.85,12,11.05a8,8,0,0,0,10.82,0l12-11.05,58,53.15H52.57ZM157.29,128,216,74.19V181.81Z"></path>
                </svg>
                <h2 className="text-lg font-bold text-white">消息详情</h2>
              </div>
              <button
                onClick={() => {
                  setShowDetailDialog(false);
                  setSelectedMessage(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                </svg>
              </button>
            </div>

            {/* 内容 */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* 基本信息 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-400 text-sm mb-2">消息类型</div>
                  <div className="bg-[#12140f] border border-[#2a2d27] rounded-lg p-4">
                    <div className="text-white font-medium">
                      {subscriptionTypes[selectedMessage.subscription_type]}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm mb-2">发送人数</div>
                  <div className="bg-[#12140f] border border-[#2a2d27] rounded-lg p-4">
                    <div className="text-white font-medium">
                      {selectedMessage.sent_count || 0} 人
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-gray-400 text-sm mb-2">发送时间</div>
                <div className="bg-[#12140f] border border-[#2a2d27] rounded-lg p-4">
                  <div className="text-white font-medium">
                    {new Date(selectedMessage.created_at).toLocaleString(
                      'zh-CN'
                    )}
                  </div>
                </div>
              </div>

              {/* 英文内容 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded">
                    English
                  </span>
                  <span className="text-gray-400 text-sm">英文内容</span>
                </div>
                <div className="bg-[#12140f] border border-[#2a2d27] rounded-lg p-4">
                  <h3 className="text-base font-semibold text-white mb-3">
                    {selectedMessage.title_en}
                  </h3>
                  <p className="text-gray-300 whitespace-pre-wrap">
                    {selectedMessage.content_en}
                  </p>
                </div>
              </div>

              {/* 英文图片 */}
              {console.log(
                '[Detail] 英文图片检查:',
                selectedMessage.image_en,
                '条件:',
                !!selectedMessage.image_en
              )}
              {selectedMessage.image_en && (
                <div>
                  <div className="text-gray-400 text-sm mb-2">英文图片：</div>
                  <div className="bg-[#12140f] border border-[#2a2d27] rounded-lg p-2 flex justify-center items-center">
                    <img
                      src={`${FRONTEND_URL}${selectedMessage.image_en}`}
                      alt="English message"
                      className="max-w-full h-auto max-h-96 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() =>
                        setEnlargedImage(
                          `${FRONTEND_URL}${selectedMessage.image_en}`
                        )
                      }
                      onError={(e) => {
                        console.error(
                          '[Detail] 英文图片加载失败:',
                          selectedMessage.image_en
                        );
                        e.target.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log(
                          '[Detail] 英文图片加载成功:',
                          selectedMessage.image_en
                        );
                      }}
                    />
                  </div>
                </div>
              )}

              {/* 中文内容 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded">
                    中文
                  </span>
                  <span className="text-gray-400 text-sm">中文内容</span>
                </div>
                <div className="bg-[#12140f] border border-[#2a2d27] rounded-lg p-4">
                  <h3 className="text-base font-semibold text-white mb-3">
                    {selectedMessage.title_zh}
                  </h3>
                  <p className="text-gray-300 whitespace-pre-wrap">
                    {selectedMessage.content_zh}
                  </p>
                </div>
              </div>

              {/* 中文图片 */}
              {console.log(
                '[Detail] 中文图片检查:',
                selectedMessage.image_zh,
                '条件:',
                !!selectedMessage.image_zh
              )}
              {selectedMessage.image_zh && (
                <div>
                  <div className="text-gray-400 text-sm mb-2">中文图片：</div>
                  <div className="bg-[#12140f] border border-[#2a2d27] rounded-lg p-2 flex justify-center items-center">
                    <img
                      src={`${FRONTEND_URL}${selectedMessage.image_zh}`}
                      alt="Chinese message"
                      className="max-w-full h-auto max-h-96 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() =>
                        setEnlargedImage(
                          `${FRONTEND_URL}${selectedMessage.image_zh}`
                        )
                      }
                      onError={(e) => {
                        console.error(
                          '[Detail] 中文图片加载失败:',
                          selectedMessage.image_zh
                        );
                        e.target.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log(
                          '[Detail] 中文图片加载成功:',
                          selectedMessage.image_zh
                        );
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* 底部 */}
            <div className="bg-[#0a0c0a] border-t border-[#2a2d27] px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  setShowDetailDialog(false);
                  setSelectedMessage(null);
                }}
                className="px-6 py-3 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 图片放大模态框 */}
      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
          onClick={() => setEnlargedImage(null)}
        >
          <div className="relative max-w-[95vw] max-h-[95vh]">
            {/* 关闭按钮 */}
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-8 h-8"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            {/* 放大的图片 */}
            <img
              src={enlargedImage}
              alt="Enlarged view"
              className="max-w-full max-h-[95vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
